/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-64 — laying a ticket out on 384 dots of paper
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ONE renderer, used by BOTH the on-screen preview and the printer. That is the
 * whole reason this file exists separately from the drawing: if the preview and
 * the print were produced by different code they would drift, and a preview that
 * lies is worse than no preview at all.
 *
 * PURE ON PURPOSE. This produces a list of draw operations and a height; it
 * never touches a canvas. Text measurement arrives as a function, so the layout
 * — wrapping, columns, alignment, the running y — is testable in plain jest
 * without a DOM, and the canvas code that consumes it stays thin enough to read
 * in one sitting.
 */

import {
  FOOTER_TEXT,
  RECEIPT_WIDTH_DOTS,
  type ReceiptAlign,
  type ReceiptSize,
  type ReceiptTemplate,
} from "./receiptTemplate";

/** Every member of the server's `SalePaymentType`. */
export type ReceiptPaymentMethod = "CASH" | "CARD" | "BANK_TRANSFER" | "ONLINE";

/** What the server hands us for one sale (GET /shop-mode/sales/:id/receipt). */
export interface ReceiptData {
  saleId: string;
  soldAt: string;
  /** `nif` is the shop's NIF (G-203); absent or null on a shop without one. */
  shop: { name: string; address: string; nif?: string | null };
  servedBy: string;
  /**
   * G-141 (OD-215): how it was paid — one row, or cash + card. Empty on a sale
   * that completed an order (its money is the order's) and on older sales.
   */
  payments: Array<{ method: ReceiptPaymentMethod; amountEur: string }>;
  lines: Array<{
    description: string;
    quantity: string;
    /** The server's `DefaultUom`, translated for print by `uomLabel`. */
    uom: "STEM" | "UNIT" | "CM" | "ROLL" | null;
    unitPriceEur: string;
    lineTotalEur: string;
  }>;
  totalEur: string;
}

export type DrawOp =
  | {
      kind: "text";
      text: string;
      x: number;
      y: number;
      px: number;
      bold: boolean;
      align: ReceiptAlign;
    }
  | { kind: "image"; x: number; y: number; w: number; h: number }
  | { kind: "rule"; y: number };

export interface LaidOutReceipt {
  ops: DrawOp[];
  /** Total paper height in dots — what the caller sizes the canvas to. */
  height: number;
}

/** Measures a string at a given size. Canvas in the app, a stub in tests. */
export type MeasureText = (text: string, px: number, bold: boolean) => number;

/**
 * How each payment method is named in Spanish.
 *
 * A MAP, NOT A TERNARY, and that is the fix for a real defect: the first
 * version said cash-or-else-card, so a BANK_TRANSFER sale — which G-20 allows
 * for an order's final payment, and which this ticket reaches through the
 * Action log reprint — printed "Pagado con tarjeta" on a customer's receipt.
 * A lookup keyed by the enum cannot quietly absorb a new member: an unmapped
 * one yields undefined and the line is OMITTED, which is this block's whole
 * principle (absent rather than wrong).
 */
const PAYMENT_LABELS: Record<ReceiptPaymentMethod, string> = {
  CASH: "Pagado en efectivo",
  CARD: "Pagado con tarjeta",
  BANK_TRANSFER: "Pagado por transferencia",
  ONLINE: "Pagado online",
};

/** G-141: the short names a split prints beside each amount. */
const PAYMENT_PART_LABELS: Record<ReceiptPaymentMethod, string> = {
  CASH: "Efectivo",
  CARD: "Tarjeta",
  BANK_TRANSFER: "Transferencia",
  ONLINE: "Online",
};

/**
 * One method: "Pagado en efectivo". A split: one line per part, "Efectivo
 * 20.00 €" then "Tarjeta 20.00 €" (a 58 mm ticket wraps one long line
 * mid-amount). Empty — the block is omitted — when nothing was recorded or
 * any method is one this tablet does not know.
 */
function paymentLines(payments: ReceiptData["payments"]): string[] {
  if (payments.length === 1) {
    const label = PAYMENT_LABELS[payments[0].method];
    return label ? [label] : [];
  }
  const parts = payments.map((p) => {
    const name = PAYMENT_PART_LABELS[p.method];
    return name ? `${name} ${p.amountEur} €` : null;
  });
  return parts.every((p): p is string => p !== null) ? parts : [];
}

/**
 * Units of measure in Spanish, singular and plural.
 *
 * The app's own interface is English (G-27) and `shopFloorFormat.uomLabel`
 * serves that; the PRINTED ticket is Spanish (owner, 2026-09-10). Two
 * languages, two rules — not one rule implemented twice. Abbreviated because
 * 58 mm of paper has no room for "unidades" beside a quantity and a price.
 */
const UOM_LABELS: Record<
  NonNullable<ReceiptData["lines"][number]["uom"]>,
  [one: string, many: string]
> = {
  STEM: ["tallo", "tallos"],
  UNIT: ["ud.", "uds."],
  CM: ["cm", "cm"],
  ROLL: ["rollo", "rollos"],
};

export function uomLabel(
  uom: ReceiptData["lines"][number]["uom"],
  quantity: string,
): string {
  if (!uom) return "";
  const pair = UOM_LABELS[uom];
  // An unknown unit prints nothing rather than an English enum name.
  if (!pair) return "";
  return Number(quantity) === 1 ? pair[0] : pair[1];
}

/** Margin kept clear of both edges; thermal heads are unreliable at the rim. */
const MARGIN = 8;
const CONTENT_WIDTH = RECEIPT_WIDTH_DOTS - MARGIN * 2;

const FONT_PX: Record<ReceiptSize, number> = {
  small: 18,
  normal: 22,
  large: 30,
};

/** Generous enough that descenders never clip on a 1-bit render. */
const LINE_HEIGHT = 1.32;

function xFor(align: ReceiptAlign): number {
  if (align === "center") return RECEIPT_WIDTH_DOTS / 2;
  if (align === "right") return RECEIPT_WIDTH_DOTS - MARGIN;
  return MARGIN;
}

/**
 * Greedy word wrap. Falls back to breaking mid-word for a single token wider
 * than the paper — a product code with no spaces must not run off the edge and
 * silently lose its tail.
 */
export function wrapText(
  text: string,
  maxWidth: number,
  px: number,
  bold: boolean,
  measure: MeasureText,
): string[] {
  const out: string[] = [];
  for (const paragraph of text.split("\n")) {
    let line = "";
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const candidate = line ? `${line} ${word}` : word;
      if (measure(candidate, px, bold) <= maxWidth) {
        line = candidate;
        continue;
      }
      if (line) out.push(line);
      if (measure(word, px, bold) <= maxWidth) {
        line = word;
        continue;
      }
      // One word wider than the roll: break it by character.
      let chunk = "";
      for (const ch of word) {
        if (measure(chunk + ch, px, bold) > maxWidth && chunk) {
          out.push(chunk);
          chunk = ch;
        } else {
          chunk += ch;
        }
      }
      line = chunk;
    }
    out.push(line);
  }
  return out.length ? out : [""];
}

/**
 * Lays the whole ticket out.
 *
 * The footer is APPENDED HERE, always, after every block the template asked
 * for — it is not in `template.blocks` and cannot be reordered, hidden or
 * reworded (OD-13). This is one of the three locks on it.
 */
export function layoutReceipt(
  template: ReceiptTemplate,
  data: ReceiptData,
  measure: MeasureText,
  /** Logo aspect (h/w), so the image reserves the right height. */
  logoAspect: number | null,
): LaidOutReceipt {
  const ops: DrawOp[] = [];
  let y = MARGIN;

  const writeLines = (
    lines: string[],
    px: number,
    bold: boolean,
    align: ReceiptAlign,
  ) => {
    for (const line of lines) {
      y += Math.round(px * LINE_HEIGHT);
      ops.push({
        kind: "text",
        text: line,
        x: xFor(align),
        y,
        px,
        bold,
        align,
      });
    }
  };

  const writeParagraph = (
    text: string,
    px: number,
    bold: boolean,
    align: ReceiptAlign,
  ) =>
    writeLines(
      wrapText(text, CONTENT_WIDTH, px, bold, measure),
      px,
      bold,
      align,
    );

  for (const block of template.blocks) {
    if (!block.visible) continue;
    const px = FONT_PX[block.size];

    switch (block.id) {
      case "logo": {
        // No logo uploaded is not an error — the block simply prints nothing.
        if (!template.logoKey || logoAspect === null) break;
        const w = Math.round(RECEIPT_WIDTH_DOTS * template.logoScale);
        const h = Math.round(w * logoAspect);
        const x =
          block.align === "left"
            ? MARGIN
            : block.align === "right"
              ? RECEIPT_WIDTH_DOTS - MARGIN - w
              : Math.round((RECEIPT_WIDTH_DOTS - w) / 2);
        ops.push({ kind: "image", x, y, w, h });
        y += h + 6;
        break;
      }

      case "shopName":
        writeParagraph(data.shop.name, px, true, block.align);
        break;

      case "address":
        writeParagraph(data.shop.address, px, false, block.align);
        break;

      case "nif":
        // A shop with no NIF prints nothing here — the block is not an error,
        // and the ticket stays a courtesy ticket either way (OD-402).
        if (data.shop.nif) writeParagraph(`NIF ${data.shop.nif}`, px, false, block.align);
        break;

      case "dateTime":
        writeParagraph(formatMadrid(data.soldAt), px, false, block.align);
        break;

      case "lines": {
        y += 4;
        ops.push({ kind: "rule", y });
        y += 4;
        for (const line of data.lines) {
          // Description on its own line(s), then the arithmetic beneath it:
          // 58 mm is too narrow to put a product name and three numbers on one
          // row without truncating the name, which is the part a customer reads.
          writeParagraph(line.description, px, false, "left");
          const qty = trimNumber(line.quantity);
          const unit = uomLabel(line.uom, qty);
          const left = `${qty}${unit ? ` ${unit}` : ""} × ${line.unitPriceEur} €`;
          const right = `${line.lineTotalEur} €`;
          y += Math.round(px * LINE_HEIGHT);
          ops.push({
            kind: "text",
            text: left,
            x: MARGIN,
            y,
            px: FONT_PX.small,
            bold: false,
            align: "left",
          });
          ops.push({
            kind: "text",
            text: right,
            x: RECEIPT_WIDTH_DOTS - MARGIN,
            y,
            px: FONT_PX.small,
            bold: false,
            align: "right",
          });
        }
        y += 4;
        ops.push({ kind: "rule", y });
        y += 4;
        break;
      }

      case "total":
        writeLines([`TOTAL  ${data.totalEur} €`], px, true, block.align);
        break;

      case "payment": {
        // Absent rather than wrong: an older sale may carry no payment type,
        // and inventing "efectivo" on a customer's ticket would be a lie.
        for (const line of paymentLines(data.payments)) {
          writeParagraph(line, px, false, block.align);
        }
        break;
      }

      case "servedBy":
        writeParagraph(`Le atendió ${data.servedBy}`, px, false, block.align);
        break;

      case "message":
        if (!block.text?.trim()) break;
        writeParagraph(block.text.trim(), px, false, block.align);
        break;
    }
  }

  // ── The locked footer (OD-13) ───────────────────────────────────────────
  y += 10;
  writeParagraph(FOOTER_TEXT, FONT_PX.small, false, "center");

  // Trailing paper so the tear-off does not cut through the last line.
  y += 24;
  return { ops, height: y };
}

/** "1.000" → "1"; keeps a real fraction. Decimal noise on a ticket reads as an error. */
export function trimNumber(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return String(Number(n.toFixed(3)));
}

/** The shop trades in Barcelona; the ticket says the shop's time, not the tablet's. */
export function formatMadrid(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}
